/**
 * Support API client — F-013 (form di contatto in-app).
 *
 * Wrapper `fetch` tipizzati per gli endpoint supporto (F-013 backend):
 *  - POST /api/v1/support/ticket/upload-url → presigned PUT per l'allegato
 *  - POST /api/v1/support/ticket            → crea il ticket
 *
 * Convenzioni (mirror di `features/wallet/lib/wallet-client.ts`):
 *  - `credentials: 'include'` → il cookie di sessione viaggia con ogni request.
 *  - Errori → `SupportApiError` con `status` + `code`; `errorMessageFor(code)`
 *    mappa i codici noti su copy italiano per i toast.
 */
import type {
  CreateSupportTicketRequest,
  SupportTicketResponse,
  SupportTicketUploadUrlRequest,
  SupportTicketUploadUrlResponse,
} from '@app/shared';

const API = '/api/v1/support';

export class SupportApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SupportApiError';
    this.status = status;
    this.code = code;
  }
}

export function isSupportApiError(err: unknown): err is SupportApiError {
  return err instanceof SupportApiError;
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Controlla i campi del form.',
  SUPPORT_TICKET_RATE_LIMITED: "Hai raggiunto il limite di 5 ticket nell'ultima ora.",
  RATE_LIMIT_EXCEEDED: "Hai raggiunto il limite di ticket nell'ultima ora.",
  SUPPORT_TICKET_ATTACHMENT_INVALID: "L'allegato non è valido. Riprova con un altro file.",
  SUPPORT_TICKET_PRATICA_NOT_OWNED: 'Riferimento pratica non valido.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
};

const NETWORK_ERROR_MESSAGE = 'Errore di connessione. Riprova.';

export function errorMessageFor(err: unknown): string {
  if (isSupportApiError(err)) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return NETWORK_ERROR_MESSAGE;
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(
  res: Response,
  fallbackCode: string,
  fallbackMsg: string,
): Promise<never> {
  const body = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new SupportApiError(res.status, body.code ?? fallbackCode, body.message ?? fallbackMsg);
}

/** POST /api/v1/support/ticket/upload-url — presigned PUT per l'allegato. */
export async function getSupportTicketUploadUrl(
  input: SupportTicketUploadUrlRequest,
): Promise<SupportTicketUploadUrlResponse> {
  const res = await fetch(`${API}/ticket/upload-url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwForError(res, 'UPLOAD_URL_FAILED', "Impossibile preparare l'upload");
  return (await res.json()) as SupportTicketUploadUrlResponse;
}

/** PUT diretto del file su S3 tramite l'URL presigned. */
export async function uploadSupportAttachment(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) {
    throw new SupportApiError(res.status, 'ATTACHMENT_UPLOAD_FAILED', "Upload dell'allegato fallito");
  }
}

/** POST /api/v1/support/ticket — crea il ticket. */
export async function createSupportTicket(
  input: CreateSupportTicketRequest,
): Promise<SupportTicketResponse> {
  const res = await fetch(`${API}/ticket`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwForError(res, 'CREATE_TICKET_FAILED', 'Impossibile inviare il ticket');
  return (await res.json()) as SupportTicketResponse;
}
